import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Building2, 
  DollarSign, 
  Percent, 
  TrendingUp, 
  TrendingDown,
  Calculator,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Save,
  Download,
  ChevronRight,
  ArrowLeft,
  BarChart3,
  PieChart,
  Target,
  Shield,
  Crown,
  RefreshCw,
  Info
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency helper
const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Format percentage
const formatPercent = (value, decimals = 2) => {
  if (!value && value !== 0) return "-";
  return `${value.toFixed(decimals)}%`;
};

// Format number with commas
const formatNumber = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat('en-US').format(value);
};

// Metric Card Component
const MetricCard = ({ label, value, subtext, color = "white", icon: Icon }) => (
  <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-white/50 text-sm">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-white/40" />}
    </div>
    <div className={`font-mono text-2xl font-bold ${
      color === "green" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
      color === "red" ? "text-[#FF3B30]" :
      color === "gold" ? "text-gold" :
      "text-white"
    }`}>
      {value}
    </div>
    {subtext && <div className="text-white/40 text-xs mt-1">{subtext}</div>}
  </div>
);

// Risk Score Badge
const RiskBadge = ({ grade, score }) => {
  const colors = {
    A: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/50",
    B: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/50",
    C: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/50",
    D: "bg-red-500/20 text-[#FF3B30] border-red-500/50"
  };
  
  return (
    <div className={`px-4 py-2 border ${colors[grade] || colors.D}`}>
      <div className="text-2xl font-bold font-mono">{grade}</div>
      <div className="text-xs">Score: {score}</div>
    </div>
  );
};

// Decision Badge
const DecisionBadge = ({ decision }) => {
  const config = {
    proceed: { color: "bg-emerald-500", text: "PROCEED", icon: CheckCircle },
    renegotiate: { color: "bg-amber-500", text: "RENEGOTIATE", icon: AlertTriangle },
    reject: { color: "bg-red-500", text: "REJECT", icon: XCircle }
  };
  
  const c = config[decision] || config.reject;
  const Icon = c.icon;
  
  return (
    <div className={`${c.color} px-4 py-2 flex items-center gap-2`}>
      <Icon className="w-5 h-5 text-white" />
      <span className="font-bold text-white">{c.text}</span>
    </div>
  );
};

export default function MultifamilyUnderwritingPage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const { analysisId } = useParams();
  const tier = subscription?.tier || "starter";
  
  const [activeTab, setActiveTab] = useState("property");
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisName, setAnalysisName] = useState("");
  const [results, setResults] = useState(null);
  
  // Form state - Property Overview
  const [property, setProperty] = useState({
    property_name: "",
    address: "",
    market: "",
    submarket: "",
    year_built: new Date().getFullYear() - 20,
    total_units: 0,
    current_occupancy: 92,
    market_occupancy: 95,
    current_avg_rent: 0,
    market_avg_rent: 0,
    other_income_monthly: 0
  });
  
  // Form state - Acquisition
  const [acquisition, setAcquisition] = useState({
    purchase_price: 0,
    earnest_money: 0,
    closing_costs: 0,
    capex_budget: 0,
    hold_period_years: 5,
    exit_cap_rate: 6
  });
  
  // Form state - Financing
  const [financing, setFinancing] = useState({
    loan_type: "conventional",
    ltv_percent: 70,
    interest_rate: 6.5,
    amortization_years: 30,
    loan_term_years: 10,
    io_period_months: 0,
    origination_fee_percent: 1,
    prepayment_penalty_type: "none"
  });
  
  // Form state - Operating
  const [operating, setOperating] = useState({
    gross_potential_rent: 0,
    vacancy_rate_actual: 8,
    vacancy_rate_stabilized: 5,
    total_operating_expenses: 0,
    property_taxes: 0,
    insurance: 0,
    management_percent: 5,
    capex_reserves: 0,
    utilities: 0,
    repairs_maintenance: 0,
    admin_general: 0,
    payroll: 0
  });

  // Load existing analysis if editing
  useEffect(() => {
    if (analysisId) {
      loadAnalysis(analysisId);
    }
  }, [analysisId]);

  const loadAnalysis = async (id) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/underwriting/analyses/${id}`,
        { withCredentials: true }
      );
      
      const { inputs, results: savedResults, name } = response.data;
      
      if (inputs.property_overview) setProperty(inputs.property_overview);
      if (inputs.acquisition) setAcquisition(inputs.acquisition);
      if (inputs.financing) setFinancing(inputs.financing);
      if (inputs.operating) setOperating(inputs.operating);
      if (savedResults) setResults(savedResults);
      if (name) setAnalysisName(name);
      
    } catch (error) {
      toast.error("Failed to load analysis");
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/underwriting/analyze`,
        {
          name: analysisName || property.property_name || "New Analysis",
          property_overview: property,
          acquisition,
          financing,
          operating
        },
        { withCredentials: true }
      );
      
      setResults(response.data);
      setActiveTab("results");
      toast.success("Analysis complete!");
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const saveAnalysis = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/underwriting/save`,
        {
          name: analysisName || property.property_name || "New Analysis",
          property_overview: property,
          acquisition,
          financing,
          operating
        },
        { withCredentials: true }
      );
      
      setResults(response.data.results);
      toast.success("Analysis saved!");
      navigate(`/underwriting/${response.data.analysis_id}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Auto-calculate GPR when rent/units change
  useEffect(() => {
    if (property.current_avg_rent && property.total_units) {
      const gpr = property.current_avg_rent * property.total_units * 12;
      setOperating(prev => ({ ...prev, gross_potential_rent: gpr }));
    }
  }, [property.current_avg_rent, property.total_units]);

  // Auto-calculate total project cost
  const totalProjectCost = acquisition.purchase_price + acquisition.closing_costs + acquisition.capex_budget;
  
  // Auto-calculate loan amount and equity
  const loanAmount = acquisition.purchase_price * (financing.ltv_percent / 100);
  const equityRequired = totalProjectCost - loanAmount;

  // Feature gate
  if (tier === "starter") {
    return (
      <div className="min-h-screen bg-black flex">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className="ml-64 flex-1 p-8">
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-gold" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-4">
              Multifamily Underwriting
            </h1>
            <p className="text-white/70 mb-8">
              Institutional-grade underwriting for multifamily acquisitions. 
              Analyze deals with professional metrics, stress testing, and risk assessment.
            </p>
            <Link to="/upgrade">
              <Button className="gold-gradient-btn rounded-none font-bold px-8 h-12">
                <Crown className="w-5 h-5 mr-2" />Upgrade to Pro
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className="ml-64 flex-1 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/underwriting" className="text-white/50 hover:text-gold flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Analyses
            </Link>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">
              Multifamily Underwriting
            </h1>
            <p className="text-white/50 mt-1">Institutional-grade acquisition analysis</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Input
              value={analysisName}
              onChange={(e) => setAnalysisName(e.target.value)}
              placeholder="Analysis Name"
              className="w-64 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
            />
            <Button 
              onClick={runAnalysis}
              disabled={analyzing}
              className="gold-gradient-btn rounded-none font-bold"
            >
              {analyzing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
              {analyzing ? "Analyzing..." : "Run Analysis"}
            </Button>
            <Button 
              onClick={saveAnalysis}
              disabled={saving || !results}
              variant="outline"
              className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </div>

        {/* Quick Metrics Preview */}
        {(acquisition.purchase_price > 0 || results) && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <MetricCard 
              label="Purchase Price" 
              value={formatCurrency(acquisition.purchase_price)}
              icon={DollarSign}
            />
            <MetricCard 
              label="Total Units" 
              value={formatNumber(property.total_units)}
              subtext={acquisition.purchase_price && property.total_units ? 
                `${formatCurrency(acquisition.purchase_price / property.total_units)}/unit` : null}
              icon={Building2}
            />
            <MetricCard 
              label="Loan Amount" 
              value={formatCurrency(loanAmount)}
              subtext={`${financing.ltv_percent}% LTV`}
              icon={Percent}
            />
            <MetricCard 
              label="Equity Required" 
              value={formatCurrency(equityRequired)}
              icon={Target}
            />
            {results && (
              <>
                <MetricCard 
                  label="Cap Rate" 
                  value={formatPercent(results.valuation_metrics?.in_place_cap_rate)}
                  color={results.valuation_metrics?.in_place_cap_rate >= 6 ? "green" : "white"}
                  icon={TrendingUp}
                />
                <MetricCard 
                  label="DSCR" 
                  value={results.debt_metrics?.dscr?.toFixed(2) || "-"}
                  color={results.debt_metrics?.dscr >= 1.25 ? "green" : results.debt_metrics?.dscr >= 1.0 ? "white" : "red"}
                  icon={Shield}
                />
              </>
            )}
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto pb-2">
            <TabsList className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-1 inline-flex min-w-max">
              <TabsTrigger value="property" className="data-[state=active]:bg-gold data-[state=active]:text-black whitespace-nowrap">
                <Building2 className="w-4 h-4 mr-2" />Property
              </TabsTrigger>
              <TabsTrigger value="acquisition" className="data-[state=active]:bg-gold data-[state=active]:text-black whitespace-nowrap">
                <DollarSign className="w-4 h-4 mr-2" />Acquisition
              </TabsTrigger>
              <TabsTrigger value="financing" className="data-[state=active]:bg-gold data-[state=active]:text-black whitespace-nowrap">
                <Percent className="w-4 h-4 mr-2" />Financing
              </TabsTrigger>
              <TabsTrigger value="operating" className="data-[state=active]:bg-gold data-[state=active]:text-black whitespace-nowrap">
                <Calculator className="w-4 h-4 mr-2" />Operating
              </TabsTrigger>
              <TabsTrigger value="results" className="data-[state=active]:bg-gold data-[state=active]:text-black whitespace-nowrap" disabled={!results}>
                <BarChart3 className="w-4 h-4 mr-2" />Results
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Property Tab */}
          <TabsContent value="property" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Property Overview</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/50">Property Name</Label>
                    <Input
                      value={property.property_name}
                      onChange={(e) => setProperty({...property, property_name: e.target.value})}
                      placeholder="Oak Grove Apartments"
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/50">Address</Label>
                    <Input
                      value={property.address}
                      onChange={(e) => setProperty({...property, address: e.target.value})}
                      placeholder="123 Main St, Austin, TX 78701"
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50">Market</Label>
                      <Input
                        value={property.market}
                        onChange={(e) => setProperty({...property, market: e.target.value})}
                        placeholder="Austin"
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50">Submarket</Label>
                      <Input
                        value={property.submarket}
                        onChange={(e) => setProperty({...property, submarket: e.target.value})}
                        placeholder="North Austin"
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50">Year Built</Label>
                      <Input
                        type="number"
                        value={property.year_built}
                        onChange={(e) => setProperty({...property, year_built: parseInt(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50">Total Units</Label>
                      <Input
                        type="number"
                        value={property.total_units}
                        onChange={(e) => setProperty({...property, total_units: parseInt(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Rent & Occupancy</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50">Current Occupancy %</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={property.current_occupancy}
                        onChange={(e) => setProperty({...property, current_occupancy: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50">Market Occupancy %</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={property.market_occupancy}
                        onChange={(e) => setProperty({...property, market_occupancy: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50">Current Avg Rent ($)</Label>
                      <Input
                        type="number"
                        value={property.current_avg_rent}
                        onChange={(e) => setProperty({...property, current_avg_rent: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50">Market Avg Rent ($)</Label>
                      <Input
                        type="number"
                        value={property.market_avg_rent}
                        onChange={(e) => setProperty({...property, market_avg_rent: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/50">Other Income (Monthly) ($)</Label>
                    <Input
                      type="number"
                      value={property.other_income_monthly}
                      onChange={(e) => setProperty({...property, other_income_monthly: parseFloat(e.target.value) || 0})}
                      placeholder="Laundry, parking, etc."
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                  
                  {/* GPR Preview */}
                  {property.current_avg_rent > 0 && property.total_units > 0 && (
                    <div className="bg-black p-4 mt-4">
                      <div className="text-white/50 text-sm">Gross Potential Rent (Annual)</div>
                      <div className="text-gold font-mono text-xl font-bold">
                        {formatCurrency(property.current_avg_rent * property.total_units * 12)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <Button onClick={() => setActiveTab("acquisition")} className="gold-gradient-btn rounded-none">
                Next: Acquisition <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* Acquisition Tab */}
          <TabsContent value="acquisition" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Purchase Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/50">Purchase Price ($)</Label>
                    <Input
                      type="number"
                      value={acquisition.purchase_price}
                      onChange={(e) => setAcquisition({...acquisition, purchase_price: parseFloat(e.target.value) || 0})}
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/50">Earnest Money ($)</Label>
                    <Input
                      type="number"
                      value={acquisition.earnest_money}
                      onChange={(e) => setAcquisition({...acquisition, earnest_money: parseFloat(e.target.value) || 0})}
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/50">Closing Costs ($)</Label>
                    <Input
                      type="number"
                      value={acquisition.closing_costs}
                      onChange={(e) => setAcquisition({...acquisition, closing_costs: parseFloat(e.target.value) || 0})}
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/50">CapEx Budget ($)</Label>
                    <Input
                      type="number"
                      value={acquisition.capex_budget}
                      onChange={(e) => setAcquisition({...acquisition, capex_budget: parseFloat(e.target.value) || 0})}
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Exit Assumptions</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/50">Hold Period (Years)</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[acquisition.hold_period_years]}
                        onValueChange={([v]) => setAcquisition({...acquisition, hold_period_years: v})}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-white font-mono w-12 text-right">{acquisition.hold_period_years} yr</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/50">Exit Cap Rate (%)</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[acquisition.exit_cap_rate]}
                        onValueChange={([v]) => setAcquisition({...acquisition, exit_cap_rate: v})}
                        min={4}
                        max={10}
                        step={0.25}
                        className="flex-1"
                      />
                      <span className="text-white font-mono w-16 text-right">{acquisition.exit_cap_rate}%</span>
                    </div>
                  </div>
                </div>

                {/* Cost Summary */}
                <div className="bg-black p-4 mt-6 space-y-3">
                  <h4 className="text-white font-bold mb-3">Total Project Cost</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Purchase Price</span>
                    <span className="text-white font-mono">{formatCurrency(acquisition.purchase_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Closing Costs</span>
                    <span className="text-white font-mono">{formatCurrency(acquisition.closing_costs)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">CapEx Budget</span>
                    <span className="text-white font-mono">{formatCurrency(acquisition.capex_budget)}</span>
                  </div>
                  <div className="border-t border-white/[0.08] pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-gold font-bold">Total</span>
                      <span className="text-gold font-mono font-bold">{formatCurrency(totalProjectCost)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between mt-6">
              <Button onClick={() => setActiveTab("property")} variant="outline" className="border-white/[0.1] text-white/70">
                <ArrowLeft className="w-4 h-4 mr-2" /> Previous
              </Button>
              <Button onClick={() => setActiveTab("financing")} className="gold-gradient-btn rounded-none">
                Next: Financing <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* Financing Tab */}
          <TabsContent value="financing" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Loan Terms</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/50">Loan Type</Label>
                    <Select 
                      value={financing.loan_type}
                      onValueChange={(v) => setFinancing({...financing, loan_type: v})}
                    >
                      <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conventional">Conventional</SelectItem>
                        <SelectItem value="agency">Agency (Fannie/Freddie)</SelectItem>
                        <SelectItem value="bridge">Bridge</SelectItem>
                        <SelectItem value="cmbs">CMBS</SelectItem>
                        <SelectItem value="hard_money">Hard Money</SelectItem>
                        <SelectItem value="seller_finance">Seller Finance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/50">LTV %</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[financing.ltv_percent]}
                        onValueChange={([v]) => setFinancing({...financing, ltv_percent: v})}
                        min={50}
                        max={85}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-white font-mono w-16 text-right">{financing.ltv_percent}%</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/50">Interest Rate %</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[financing.interest_rate]}
                        onValueChange={([v]) => setFinancing({...financing, interest_rate: v})}
                        min={3}
                        max={12}
                        step={0.125}
                        className="flex-1"
                      />
                      <span className="text-white font-mono w-16 text-right">{financing.interest_rate}%</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50">Amortization (Years)</Label>
                      <Input
                        type="number"
                        value={financing.amortization_years}
                        onChange={(e) => setFinancing({...financing, amortization_years: parseInt(e.target.value) || 30})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50">Loan Term (Years)</Label>
                      <Input
                        type="number"
                        value={financing.loan_term_years}
                        onChange={(e) => setFinancing({...financing, loan_term_years: parseInt(e.target.value) || 10})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/50">IO Period (Months)</Label>
                    <Input
                      type="number"
                      value={financing.io_period_months}
                      onChange={(e) => setFinancing({...financing, io_period_months: parseInt(e.target.value) || 0})}
                      placeholder="0 for fully amortizing"
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Loan Costs</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/50">Origination Fee %</Label>
                    <Input
                      type="number"
                      step="0.25"
                      value={financing.origination_fee_percent}
                      onChange={(e) => setFinancing({...financing, origination_fee_percent: parseFloat(e.target.value) || 0})}
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/50">Prepayment Penalty</Label>
                    <Select 
                      value={financing.prepayment_penalty_type}
                      onValueChange={(v) => setFinancing({...financing, prepayment_penalty_type: v})}
                    >
                      <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="step_down">Step-Down</SelectItem>
                        <SelectItem value="yield_maintenance">Yield Maintenance</SelectItem>
                        <SelectItem value="defeasance">Defeasance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Capital Stack Preview */}
                <div className="bg-black p-4 mt-6 space-y-3">
                  <h4 className="text-white font-bold mb-3">Capital Stack</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Loan Amount</span>
                    <span className="text-white font-mono">{formatCurrency(loanAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Equity Required</span>
                    <span className="text-white font-mono">{formatCurrency(equityRequired)}</span>
                  </div>
                  
                  {/* Visual bar */}
                  <div className="h-4 flex mt-3 overflow-hidden">
                    <div 
                      className="bg-blue-500" 
                      style={{ width: `${financing.ltv_percent}%` }}
                      title={`Debt: ${financing.ltv_percent}%`}
                    />
                    <div 
                      className="bg-gold" 
                      style={{ width: `${100 - financing.ltv_percent}%` }}
                      title={`Equity: ${100 - financing.ltv_percent}%`}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-white/40">
                    <span>Debt: {financing.ltv_percent}%</span>
                    <span>Equity: {100 - financing.ltv_percent}%</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between mt-6">
              <Button onClick={() => setActiveTab("acquisition")} variant="outline" className="border-white/[0.1] text-white/70">
                <ArrowLeft className="w-4 h-4 mr-2" /> Previous
              </Button>
              <Button onClick={() => setActiveTab("operating")} className="gold-gradient-btn rounded-none">
                Next: Operating <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* Operating Tab */}
          <TabsContent value="operating" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Income</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/50">Gross Potential Rent (Annual) ($)</Label>
                    <Input
                      type="number"
                      value={operating.gross_potential_rent}
                      onChange={(e) => setOperating({...operating, gross_potential_rent: parseFloat(e.target.value) || 0})}
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                    <p className="text-white/40 text-xs mt-1">Auto-calculated from rent × units × 12</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50">Vacancy Rate (Actual) %</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={operating.vacancy_rate_actual}
                        onChange={(e) => setOperating({...operating, vacancy_rate_actual: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50">Vacancy (Stabilized) %</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={operating.vacancy_rate_stabilized}
                        onChange={(e) => setOperating({...operating, vacancy_rate_stabilized: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1"
                      />
                    </div>
                  </div>
                </div>

                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4 mt-8">Expenses</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/50">Total Operating Expenses (Annual) ($)</Label>
                    <Input
                      type="number"
                      value={operating.total_operating_expenses}
                      onChange={(e) => setOperating({...operating, total_operating_expenses: parseFloat(e.target.value) || 0})}
                      placeholder="Or itemize below"
                      className="bg-black border-white/[0.08] text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Itemized Expenses (Optional)</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50 text-sm">Property Taxes ($)</Label>
                      <Input
                        type="number"
                        value={operating.property_taxes}
                        onChange={(e) => setOperating({...operating, property_taxes: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50 text-sm">Insurance ($)</Label>
                      <Input
                        type="number"
                        value={operating.insurance}
                        onChange={(e) => setOperating({...operating, insurance: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50 text-sm">Management %</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={operating.management_percent}
                        onChange={(e) => setOperating({...operating, management_percent: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50 text-sm">CapEx Reserves ($)</Label>
                      <Input
                        type="number"
                        value={operating.capex_reserves}
                        onChange={(e) => setOperating({...operating, capex_reserves: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50 text-sm">Utilities ($)</Label>
                      <Input
                        type="number"
                        value={operating.utilities}
                        onChange={(e) => setOperating({...operating, utilities: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50 text-sm">Repairs & Maint ($)</Label>
                      <Input
                        type="number"
                        value={operating.repairs_maintenance}
                        onChange={(e) => setOperating({...operating, repairs_maintenance: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/50 text-sm">Admin & General ($)</Label>
                      <Input
                        type="number"
                        value={operating.admin_general}
                        onChange={(e) => setOperating({...operating, admin_general: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-white/50 text-sm">Payroll ($)</Label>
                      <Input
                        type="number"
                        value={operating.payroll}
                        onChange={(e) => setOperating({...operating, payroll: parseFloat(e.target.value) || 0})}
                        className="bg-black border-white/[0.08] text-white mt-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between mt-6">
              <Button onClick={() => setActiveTab("financing")} variant="outline" className="border-white/[0.1] text-white/70">
                <ArrowLeft className="w-4 h-4 mr-2" /> Previous
              </Button>
              <Button onClick={runAnalysis} disabled={analyzing} className="gold-gradient-btn rounded-none">
                {analyzing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                {analyzing ? "Analyzing..." : "Run Full Analysis"}
              </Button>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="mt-6">
            {results ? (
              <div className="space-y-6">
                {/* Investment Decision Summary Card */}
                <div className="bg-gradient-to-r from-[#0A0A0A] to-[#111] border border-white/[0.08] p-6 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4AF37]/[0.03] to-transparent pointer-events-none" />
                  <h3 className="font-heading text-sm font-bold text-white/50 uppercase tracking-wider mb-4">Investment Recommendation</h3>
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative">
                    <div className="flex items-center gap-6">
                      <RiskBadge 
                        grade={results.risk_assessment?.grade} 
                        score={results.risk_assessment?.deal_score} 
                      />
                      <div className="space-y-1">
                        <div className="text-white/50 text-xs">Risk Level</div>
                        <div className="text-white text-xl font-bold">{results.risk_assessment?.risk_level}</div>
                        <div className="text-white/40 text-xs">
                          {results.decision?.recommendation || "Evaluate further"}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-6">
                      <div>
                        <div className="text-white/50 text-xs">Projected IRR</div>
                        <div className={`text-xl font-mono font-bold ${
                          results.investor_metrics?.irr >= 15 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 
                          results.investor_metrics?.irr >= 12 ? 'text-white' : 'text-[#FF3B30]'
                        }`}>
                          {formatPercent(results.investor_metrics?.irr)}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/50 text-xs">Cash-on-Cash</div>
                        <div className={`text-xl font-mono font-bold ${
                          results.investor_metrics?.cash_on_cash_return >= 8 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-white'
                        }`}>
                          {formatPercent(results.investor_metrics?.cash_on_cash_return)}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/50 text-xs">Equity Multiple</div>
                        <div className={`text-xl font-mono font-bold ${
                          results.investor_metrics?.equity_multiple >= 2 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-white'
                        }`}>
                          {results.investor_metrics?.equity_multiple?.toFixed(2)}x
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-white/50 text-xs flex items-center gap-1">
                            Maximum Offer Price
                            <span title="The highest purchase price the investment can support based on current underwriting assumptions while maintaining target return thresholds.">
                              <Info className="w-3 h-3 text-white/30 cursor-help" />
                            </span>
                          </div>
                          <div className="text-gold text-xl font-mono font-bold">
                            {formatCurrency(results.decision?.maximum_offer_price)}
                          </div>
                        </div>
                        <DecisionBadge decision={results.decision?.decision} />
                      </div>
                    </div>
                  </div>
                  
                  {results.decision?.reasons?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/[0.08]">
                      <div className="text-white/50 text-sm mb-2">Considerations:</div>
                      <ul className="list-disc list-inside text-white/70 text-sm">
                        {results.decision.reasons.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard 
                    label="NOI" 
                    value={formatCurrency(results.income_metrics?.net_operating_income)}
                    icon={DollarSign}
                  />
                  <MetricCard 
                    label="Cap Rate" 
                    value={formatPercent(results.valuation_metrics?.in_place_cap_rate)}
                    color={results.valuation_metrics?.in_place_cap_rate >= 6 ? "green" : "white"}
                    icon={Percent}
                  />
                  <MetricCard 
                    label="DSCR" 
                    value={results.debt_metrics?.dscr?.toFixed(2)}
                    color={results.debt_metrics?.dscr >= 1.25 ? "green" : results.debt_metrics?.dscr >= 1.0 ? "white" : "red"}
                    subtext={results.debt_metrics?.dscr >= 1.25 ? "Healthy" : results.debt_metrics?.dscr >= 1.10 ? "Watch" : results.debt_metrics?.dscr >= 1.0 ? "Risk" : "Negative CF"}
                    icon={Shield}
                  />
                  <MetricCard 
                    label="Cash-on-Cash" 
                    value={formatPercent(results.investor_metrics?.cash_on_cash_return)}
                    color={results.investor_metrics?.cash_on_cash_return >= 8 ? "green" : "white"}
                    icon={TrendingUp}
                  />
                  <MetricCard 
                    label="IRR" 
                    value={formatPercent(results.investor_metrics?.irr)}
                    color={results.investor_metrics?.irr >= 15 ? "green" : results.investor_metrics?.irr >= 12 ? "white" : "red"}
                    icon={TrendingUp}
                  />
                  <MetricCard 
                    label="Equity Multiple" 
                    value={`${results.investor_metrics?.equity_multiple?.toFixed(2)}x`}
                    color={results.investor_metrics?.equity_multiple >= 2 ? "green" : "white"}
                    subtext={results.investor_metrics?.equity_multiple >= 2 ? "Strong" : results.investor_metrics?.equity_multiple >= 1.5 ? "Moderate" : results.investor_metrics?.equity_multiple >= 1.0 ? "Break-even" : "Loss"}
                    icon={Target}
                  />
                  <MetricCard 
                    label="Break-Even Occ." 
                    value={formatPercent(results.debt_metrics?.break_even_occupancy)}
                    color={results.debt_metrics?.break_even_occupancy <= 85 ? "green" : "red"}
                    icon={BarChart3}
                  />
                  <MetricCard 
                    label="Debt Yield" 
                    value={formatPercent(results.debt_metrics?.debt_yield)}
                    color={results.debt_metrics?.debt_yield >= 10 ? "green" : "white"}
                    icon={Percent}
                  />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cash Flow Projection Chart */}
                  <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                    <h3 className="font-heading text-sm font-bold text-white uppercase tracking-wider mb-4">Cash Flow Projection</h3>
                    {results.noi_projections?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={results.noi_projections}>
                          <defs>
                            <linearGradient id="noiGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#00FF88" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#00FF88" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="cfGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="year" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => `Y${v}`} />
                          <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px' }}
                            labelFormatter={(v) => `Year ${v}`}
                            formatter={(v) => [formatCurrency(v)]}
                          />
                          <Area type="monotone" dataKey="noi" stroke="#00FF88" fill="url(#noiGradient)" name="NOI" strokeWidth={2} />
                          <Area type="monotone" dataKey="cash_flow" stroke="#D4AF37" fill="url(#cfGradient)" name="Cash Flow" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-white/30 text-sm">No projection data</div>
                    )}
                  </div>

                  {/* Equity Growth Chart */}
                  <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                    <h3 className="font-heading text-sm font-bold text-white uppercase tracking-wider mb-4">Equity Growth</h3>
                    {results.equity_projections?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={[
                          { name: "Initial", value: results.debt_metrics?.equity_required || 0 },
                          ...results.equity_projections.map(ep => ({ name: `Y${ep.year}`, value: ep.equity_value }))
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                          <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px' }}
                            formatter={(v) => [formatCurrency(v), "Equity Value"]}
                          />
                          <Bar dataKey="value" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-white/30 text-sm">No projection data</div>
                    )}
                  </div>
                </div>

                {/* Stress Tests */}
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Stress Testing</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/[0.08]">
                          <th className="text-left p-3 text-white/50">Scenario</th>
                          <th className="text-right p-3 text-white/50">NOI</th>
                          <th className="text-right p-3 text-white/50">DSCR</th>
                          <th className="text-left p-3 text-white/50">Status</th>
                          <th className="text-left p-3 text-white/50">Flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.stress_tests?.map((st, i) => {
                          const dscrStatus = st.dscr >= 1.25 ? { label: "Healthy", color: "text-[#00FF88]" } :
                                            st.dscr >= 1.10 ? { label: "Watch", color: "text-[#FF9F0A]" } :
                                            st.dscr >= 1.0 ? { label: "Risk", color: "text-[#FF3B30]" } :
                                            { label: "Negative CF", color: "text-[#FF3B30]" };
                          return (
                            <tr key={i} className="border-b border-white/[0.06]">
                              <td className="p-3 text-white font-medium">{st.scenario_name}</td>
                              <td className="p-3 text-right font-mono font-bold text-white">{formatCurrency(st.noi)}</td>
                              <td className={`p-3 text-right font-mono font-bold ${
                                st.dscr >= 1.25 ? 'text-[#00FF88]' : st.dscr >= 1.0 ? 'text-[#FF9F0A]' : 'text-[#FF3B30]'
                              }`}>
                                {st.dscr?.toFixed(2)}
                              </td>
                              <td className="p-3">
                                <span className={`text-xs font-semibold ${dscrStatus.color}`}>{dscrStatus.label}</span>
                              </td>
                              <td className="p-3">
                                {st.flags?.map((f, j) => (
                                  <span key={j} className="inline-block bg-red-500/20 text-[#FF3B30] text-xs px-2 py-1 mr-1 mb-1" title={
                                    f.includes("Coverage Weakening") ? "DSCR between 1.10 and 1.25 — monitor closely" :
                                    f.includes("Debt Coverage Risk") ? "DSCR between 1.00 and 1.10 — significant risk" :
                                    f.includes("Negative Cash Flow") ? "DSCR below 1.00 — deal loses money" :
                                    f.includes("Exit Value Reduced") ? "Higher exit cap rate reduces sale proceeds" :
                                    f.includes("Debt Service Pressure") ? "Higher interest rates increase debt service burden" :
                                    f
                                  }>
                                    {f}
                                  </span>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Risk Categories */}
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Risk Categories</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { label: "Market Strength", value: results.risk_assessment?.market_strength },
                      { label: "DSCR Stability", value: results.risk_assessment?.dscr_stability },
                      { label: "Return Profile", value: results.risk_assessment?.return_profile },
                      { label: "Leverage Risk", value: results.risk_assessment?.leverage_risk },
                      { label: "Exit Risk", value: results.risk_assessment?.exit_risk }
                    ].map((item, i) => (
                      <div key={i} className="text-center">
                        <div className="text-white/50 text-sm mb-2">{item.label}</div>
                        <div className="relative h-2 bg-white/[0.08] rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${item.value >= 70 ? 'bg-emerald-500' : item.value >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${item.value}%` }}
                          />
                        </div>
                        <div className="text-white font-mono text-sm mt-1">{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Capital Stack */}
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Capital Stack</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-white/50">Senior Debt</span>
                          <span className="text-white font-mono">{formatCurrency(results.capital_stack?.senior_debt)} ({results.capital_stack?.senior_debt_percent}%)</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">Common Equity</span>
                          <span className="text-white font-mono">{formatCurrency(results.capital_stack?.common_equity)} ({results.capital_stack?.common_equity_percent}%)</span>
                        </div>
                        <div className="border-t border-white/[0.08] pt-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-white/50">GP Co-Invest (10%)</span>
                            <span className="text-white font-mono">{formatCurrency(results.capital_stack?.gp_equity)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-white/50">LP Equity (90%)</span>
                            <span className="text-white font-mono">{formatCurrency(results.capital_stack?.lp_equity)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-black p-4">
                      <div className="text-white/50 text-sm mb-2">Waterfall Structure</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/70">Preferred Return</span>
                          <span className="text-gold font-mono">{results.capital_stack?.preferred_return}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Promote Split</span>
                          <span className="text-gold font-mono">{results.capital_stack?.promote_split}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4">
                  <Button variant="outline" className="border-white/[0.1] text-white/70">
                    <Download className="w-4 h-4 mr-2" /> Export PDF
                  </Button>
                  <Button onClick={saveAnalysis} disabled={saving} className="gold-gradient-btn rounded-none">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Analysis"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <Calculator className="w-16 h-16 text-white/40 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold text-white mb-2">No Results Yet</h3>
                <p className="text-white/50 mb-6">Complete the input forms and run the analysis to see results.</p>
                <Button onClick={() => setActiveTab("property")} className="gold-gradient-btn rounded-none">
                  Start with Property Info
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
